import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as cruisesApi from './cruises.api';

import type {
  CreateCruiseCabinRateRequest,
  CreateCruiseCabinRequest,
  CreateCruiseRequest,
  CruiseApprovalRequest,
  CruiseCabinRateListQuery,
  CruiseListQuery,
  UpdateCruiseCabinRateRequest,
  UpdateCruiseCabinRequest,
  UpdateCruiseRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: CruiseListQuery) => ['cruises', 'list', query] as const,
  detail: (id: string) => ['cruises', 'detail', id] as const,
  cabins: (cruiseId: string) => ['cruises', 'cabins', cruiseId] as const,
  rates: (cruiseId: string, query: CruiseCabinRateListQuery) =>
    ['cruises', 'rates', cruiseId, query] as const,
};

export const useCruisesList = (query: CruiseListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => cruisesApi.listCruises(query),
    staleTime: 30_000,
  });

export const useCruiseDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => cruisesApi.getCruise(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateCruise = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCruiseRequest) => cruisesApi.createCruise(payload),
    onSuccess: (cruise) => {
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      toast.success(`Added ${cruise.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add cruise'),
  });
};

export const useUpdateCruise = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCruiseRequest }) =>
      cruisesApi.updateCruise(id, payload),
    onSuccess: (cruise) => {
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(cruise.id) });
      toast.success('Cruise updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update cruise'),
  });
};

export const useApproveCruise = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CruiseApprovalRequest }) =>
      cruisesApi.approveCruise(id, payload),
    onSuccess: (cruise, vars) => {
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(cruise.id) });
      toast.success(vars.payload.approve ? 'Cruise approved' : 'Approval revoked');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteCruise = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cruisesApi.deleteCruise(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      toast.success('Cruise deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete cruise'),
  });
};

export const useDuplicateCruise = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cruisesApi.duplicateCruise(id),
    onSuccess: (cruise) => {
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      toast.success(`Duplicated as "${cruise.name}" (draft)`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to duplicate cruise'),
  });
};

export const useBulkDeleteCruises = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => cruisesApi.bulkDeleteCruises(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cruises'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete cruises'),
  });
};

// ----- Cabins -----

export const useCruiseCabins = (cruiseId: string | null | undefined) =>
  useQuery({
    queryKey: QK.cabins(cruiseId ?? ''),
    queryFn: () => cruisesApi.listCruiseCabins(cruiseId as string),
    staleTime: 30_000,
    enabled: Boolean(cruiseId),
  });

export const useCreateCruiseCabin = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCruiseCabinRequest) =>
      cruisesApi.createCruiseCabin(cruiseId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.cabins(cruiseId) });
      qc.invalidateQueries({ queryKey: QK.detail(cruiseId) });
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      toast.success('Cabin added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add cabin'),
  });
};

export const useUpdateCruiseCabin = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cabinId, payload }: { cabinId: string; payload: UpdateCruiseCabinRequest }) =>
      cruisesApi.updateCruiseCabin(cruiseId, cabinId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.cabins(cruiseId) });
      qc.invalidateQueries({ queryKey: QK.detail(cruiseId) });
      toast.success('Cabin updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update cabin'),
  });
};

export const useDeleteCruiseCabin = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cabinId: string) => cruisesApi.deleteCruiseCabin(cruiseId, cabinId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.cabins(cruiseId) });
      qc.invalidateQueries({ queryKey: QK.detail(cruiseId) });
      qc.invalidateQueries({ queryKey: ['cruises', 'list'] });
      toast.success('Cabin removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove cabin'),
  });
};

// ----- Rates -----

export const useCruiseRates = (
  cruiseId: string | null | undefined,
  query: CruiseCabinRateListQuery,
) =>
  useQuery({
    queryKey: QK.rates(cruiseId ?? '', query),
    queryFn: () => cruisesApi.listCruiseRates(cruiseId as string, query),
    staleTime: 30_000,
    enabled: Boolean(cruiseId),
  });

const invalidateRates = (qc: ReturnType<typeof useQueryClient>, cruiseId: string): void => {
  qc.invalidateQueries({ queryKey: ['cruises', 'rates', cruiseId] });
  qc.invalidateQueries({ queryKey: QK.detail(cruiseId) });
};

export const useCreateCruiseRate = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCruiseCabinRateRequest) =>
      cruisesApi.createCruiseRate(cruiseId, payload),
    onSuccess: () => {
      invalidateRates(qc, cruiseId);
      toast.success('Rate added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add rate'),
  });
};

export const useUpdateCruiseRate = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rateId, payload }: { rateId: string; payload: UpdateCruiseCabinRateRequest }) =>
      cruisesApi.updateCruiseRate(cruiseId, rateId, payload),
    onSuccess: () => {
      invalidateRates(qc, cruiseId);
      toast.success('Rate updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update rate'),
  });
};

export const useDeleteCruiseRate = (cruiseId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rateId: string) => cruisesApi.deleteCruiseRate(cruiseId, rateId),
    onSuccess: () => {
      invalidateRates(qc, cruiseId);
      toast.success('Rate removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove rate'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
